import React, { useCallback, useState } from "react";

import { Button, Checkbox, Drawer, FormControlLabel, Stack, TextField, Typography } from "@mui/material";
import { SimpleHeadMui } from "components";
import { SelectAutocomplete } from "components/forms";
import { RoleComponentGuard } from "components/guards/Role-component.guard";
import { defaultDataroomFilesFilters } from "constants/datarooms";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useFormik } from "formik";
import { useDateFormatSelect } from "store";
import { DataroomFilesFilters, FilterOptions } from "store/types";

import { RoleName } from "../../../constants";
import { DateRangePicker } from "../DateRangePicker";

dayjs.extend(utc);

interface Props {
  onClose: () => void;
  isOpen: boolean;
  filters: DataroomFilesFilters;
  setFilters: (callback: (filters: DataroomFilesFilters) => DataroomFilesFilters) => void;
  allFilterOptions: { accounts: string[]; docTypes: string[]; deals: string[]; altiusDeals: string[] };
  docType?: string;
  accName?: string;
  refetchFiltersOptions: (filters: DataroomFilesFilters) => Promise<FilterOptions>;
}

export const FiltersDrawer: React.FC<Props> = ({
  isOpen,
  onClose,
  filters,
  setFilters,
  allFilterOptions,
  docType,
  accName,
  refetchFiltersOptions
}) => {
  const { accounts, docTypes, deals, altiusDeals } = allFilterOptions;

  const dateFormat = useDateFormatSelect();

  const customDateRanges = {
    Today: { from: dayjs.utc().startOf("day"), to: dayjs.utc().endOf("day") },
    Yesterday: {
      from: dayjs.utc().subtract(1, "day").startOf("day"),
      to: dayjs.utc().subtract(1, "day").endOf("day")
    },
    "Last 2 days": { from: dayjs.utc().subtract(1, "day").startOf("day"), to: dayjs.utc().endOf("day") },
    "Last 3 days": { from: dayjs.utc().subtract(2, "day").startOf("day"), to: dayjs.utc().endOf("day") },
    "Last 4 days": { from: dayjs.utc().subtract(3, "day").startOf("day"), to: dayjs.utc().endOf("day") },
    "Last 5 days": { from: dayjs.utc().subtract(4, "day").startOf("day"), to: dayjs.utc().endOf("day") },
    "Last 7 days": { from: dayjs.utc().subtract(6, "day").startOf("day"), to: dayjs.utc().endOf("day") },
    "Last 30 days": { from: dayjs.utc().subtract(29, "day").startOf("day"), to: dayjs.utc().endOf("day") },
    "This Month": { from: dayjs.utc().startOf("month"), to: dayjs.utc().endOf("month") },
    "Last Month": {
      from: dayjs.utc().subtract(1, "month").startOf("month"),
      to: dayjs.utc().subtract(1, "month").endOf("month")
    }
  };

  const [enableReinitialize, setEnableReinitialize] = useState<boolean>(true);

  const { ...rest } = filters;

  const { values, handleSubmit, resetForm, setValues } = useFormik<DataroomFilesFilters>({
    initialValues: { ...rest },
    onSubmit: async (result) => {
      const res = await refetchFiltersOptions(result);

      const updatedResult = {
        ...result,
        accounts: result.accounts.filter((item) => res.accountNameValues.includes(item)),
        altiusDeals: result.altiusDeals.filter((item) => res.altiusDealNameValues.includes(item)),
        deals: result.deals.filter((item) => res.dealNameValues.includes(item)),
        docTypes: result.docTypes.filter((item) => res.docTypeValues.includes(item))
      };

      setFilters((prevState) => ({ ...prevState, ...updatedResult }));
      setEnableReinitialize(true);
      onClose();
    },
    enableReinitialize: enableReinitialize
  });

  const setFieldValueAndRefetch = async (field: string, value: any) => {
    const updatedState = { ...values, ...{ [field]: value } };

    const res = await refetchFiltersOptions(updatedState);

    // Exclude item from selected if it is not available for select
    updatedState.accounts = updatedState.accounts.filter((item) => res.accountNameValues.includes(item));

    // Exclude item from selected if it is not available for select
    updatedState.altiusDeals = updatedState.altiusDeals.filter((item) => res.altiusDealNameValues.includes(item));

    // Exclude item from selected if it is not available for select
    updatedState.deals = updatedState.deals.filter((item) => res.dealNameValues.includes(item));

    // Exclude item from selected if it is not available for select
    updatedState.docTypes = updatedState.docTypes.filter((item) => res.docTypeValues.includes(item));

    setValues(updatedState);
  };

  const clearForm = useCallback(() => {
    setEnableReinitialize(false);
    const defaultValues = {
      ...defaultDataroomFilesFilters,
      ...(docType && { docTypes: [docType] }),
      ...(accName && { accounts: [accName] })
    };

    resetForm({ values: defaultValues });
  }, [docType, accName, resetForm]);

  const handleClose = useCallback(() => {
    refetchFiltersOptions(filters);
    setValues(filters);
    onClose();
  }, [filters]);

  return (
    <Drawer sx={{ ".MuiPaper-root": { borderRadius: "0 20px 20px 0" } }} open={isOpen} onClose={handleClose}>
      <form
        onSubmit={handleSubmit}
        style={{
          minWidth: "430px",
          maxWidth: "450px",
          height: "100%",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column"
        }}
      >
        <SimpleHeadMui onIconClick={onClose}>Filters</SimpleHeadMui>
        <Stack sx={{ gap: "24px", p: "18px 30px", flexGrow: 1, overflowY: "auto" }}>
          <SelectAutocomplete
            options={deals || []}
            value={values.deals}
            multiple
            selectAll
            setValue={(option) => setFieldValueAndRefetch("deals", option)}
            renderInput={(params) => <TextField {...params} label="Deal" />}
            disableCloseOnSelect
          />
          <SelectAutocomplete
            options={altiusDeals || []}
            value={values.altiusDeals}
            multiple
            selectAll
            setValue={(option) => setFieldValueAndRefetch("altiusDeals", option)}
            renderInput={(params) => <TextField {...params} label="Altius Deal" />}
            disableCloseOnSelect
          />
          <SelectAutocomplete
            options={accounts || []}
            value={values.accounts}
            multiple
            selectAll
            setValue={(option) => setFieldValueAndRefetch("accounts", option)}
            renderInput={(params) => <TextField {...params} label="Account Name" />}
            disableCloseOnSelect
            disabled={Boolean(accName)}
          />
          <SelectAutocomplete
            options={docTypes || []}
            value={values.docTypes}
            multiple
            selectAll
            setValue={(option) => setFieldValueAndRefetch("docTypes", option)}
            renderInput={(params) => <TextField {...params} label="Document Type" />}
            disableCloseOnSelect
            disabled={Boolean(docType)}
          />

          <DateRangePicker
            label="Index Date"
            value={values.createdAtDate || undefined}
            onChange={(range) => setFieldValueAndRefetch("createdAtDate", range)}
            dateFormat={dateFormat}
            customRanges={customDateRanges}
          />

          <DateRangePicker
            label="Sync Date"
            value={values.syncAtDate || undefined}
            onChange={(range) => setFieldValueAndRefetch("syncAtDate", range)}
            dateFormat={dateFormat}
            customRanges={customDateRanges}
          />

          <DateRangePicker
            label="Effective Date"
            value={values.effectiveAtDate || undefined}
            onChange={(range) => setFieldValueAndRefetch("effectiveAtDate", range)}
            dateFormat={dateFormat}
            customRanges={customDateRanges}
          />

          {window.location.search.includes("showAllColumns=1") && (
            <>
              <DateRangePicker
                label="Upload Date"
                value={values.uploadedAtDate || undefined}
                onChange={(range) => setFieldValueAndRefetch("uploadedAtDate", range)}
                dateFormat={dateFormat}
                customRanges={customDateRanges}
              />
            </>
          )}

          <FormControlLabel
            control={
              <Checkbox
                onChange={(e) => setFieldValueAndRefetch("showLatest", e.currentTarget.checked)}
                checked={values.showLatest}
              />
            }
            label="Show Latest"
          />
          <RoleComponentGuard availableRoles={[RoleName.Admin]}>
            <Stack direction="row" sx={{ gap: "16px" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    onChange={(e) => setFieldValueAndRefetch("notSynced", e.currentTarget.checked)}
                    checked={values.notSynced.valueOf()}
                  />
                }
                label="Not Synced to Altius"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    onChange={(e) => setFieldValueAndRefetch("synced", e.currentTarget.checked)}
                    checked={values.synced.valueOf()}
                  />
                }
                label="Synced to Altius"
              />
            </Stack>
          </RoleComponentGuard>
        </Stack>
        <Stack
          direction="row"
          sx={{
            boxShadow: "0 -1px 5px 1px rgba(0,0,0,.08)",
            p: "18px 30px",
            justifyContent: "space-between",
            alignItems: "center"
          }}
        >
          <Typography sx={{ cursor: "pointer", textDecoration: "underline", fontWeight: 500 }} onClick={clearForm}>
            Clear All Filters
          </Typography>
          <Stack direction="row" sx={{ alignItems: "center", gap: "18px" }}>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit" variant="contained">
              Apply
            </Button>
          </Stack>
        </Stack>
      </form>
    </Drawer>
  );
};
